/**
 * O que faz: Renderiza a ferramenta completa de diagnóstico SEO de Perfil.
 * Onde usa: Rota protegida /seo-perfil.
 * Conexões: useSeoProfileAnalysis, SeoProfileForm e SeoProfileResult.
 * Tabelas: seo_profile_analyses via hook.
 * Data: 14/05/2026
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SeoProfileForm } from './SeoProfileForm';
import { SeoProfileResult } from './SeoProfileResult';
import { useSeoProfileAnalysis } from '@/hooks/useSeoProfileAnalysis';
import type { SeoProfileAnalysisResult } from '@/types/seoProfile';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowRight, ChevronDown, ChevronUp, Clock, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

const sourceLabel: Record<string, string> = {
  google_business: 'Google',
  website: 'Site',
  instagram: 'Instagram',
  doctoralia: 'Doctoralia',
  general_search: 'Busca',
};

function scoreColor(score: number | null | undefined): string {
  if (!score) return 'text-muted-foreground';
  if (score >= 70) return 'text-emerald-600';
  if (score >= 40) return 'text-amber-500';
  return 'text-red-500';
}

function AnalyzingCard({
  step,
  onBackground,
}: {
  step: string;
  onBackground: () => void;
}) {
  return (
    <Card className="border-dashed shadow-sm">
      <CardContent className="flex flex-col items-center gap-5 py-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-4 ring-emerald-100">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">{step}</p>
          <p className="text-xs text-muted-foreground">Isso pode levar até 2 minutos dependendo do volume de avaliações</p>
        </div>
        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={onBackground}>
          Continuar usando o sistema enquanto analisamos
          <ArrowRight className="ml-1.5 h-3 w-3" />
        </Button>
      </CardContent>
    </Card>
  );
}

function BackgroundBanner({ step }: { step: string }) {
  return (
    <Card className="border-emerald-200 bg-emerald-50">
      <CardContent className="flex items-center gap-3 py-3">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-emerald-600" />
        <p className="text-xs text-emerald-800">
          <span className="font-medium">Análise em andamento:</span> {step}
        </p>
      </CardContent>
    </Card>
  );
}

function HistoryItem({
  item,
  isActive,
  onClick,
}: {
  item: SeoProfileAnalysisResult;
  isActive: boolean;
  onClick: () => void;
}) {
  const timeAgo = item.created_at
    ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: ptBR })
    : '';

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60',
        isActive ? 'border-emerald-300 bg-emerald-50' : 'border-transparent bg-muted/30',
      )}
    >
      <Badge variant="outline" className="shrink-0 text-xs">
        {sourceLabel[item.analysis_source] ?? item.analysis_source}
      </Badge>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs text-muted-foreground">{item.summary?.slice(0, 60) ?? 'Sem resumo'}</p>
      </div>
      <div className="shrink-0 text-right">
        {typeof item.overall_score === 'number' && (
          <p className={cn('text-sm font-bold tabular-nums', scoreColor(item.overall_score))}>
            {item.overall_score}
          </p>
        )}
        <p className="text-[10px] text-muted-foreground">{timeAgo}</p>
      </div>
    </button>
  );
}

function HistoryDropdown({
  items,
  activeId,
  onSelect,
}: {
  items: SeoProfileAnalysisResult[];
  activeId?: string;
  onSelect: (item: SeoProfileAnalysisResult) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Análises anteriores
          </span>
          <Badge variant="secondary" className="text-xs px-1.5 py-0">{items.length}</Badge>
        </div>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>

      {open && (
        <div className="border-t px-2 pb-2 pt-1 space-y-0.5">
          {items.slice(0, 6).map((item) => {
            const timeAgo = item.created_at
              ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: ptBR })
              : '';
            return (
              <button
                key={item.id}
                onClick={() => { onSelect(item); setOpen(false); }}
                className={cn(
                  'w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted/60',
                  activeId === item.id ? 'bg-emerald-50 text-emerald-900' : '',
                )}
              >
                <Badge variant="outline" className="shrink-0 text-xs">
                  {sourceLabel[item.analysis_source] ?? item.analysis_source}
                </Badge>
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {item.summary ? item.summary.slice(0, 55) + '…' : 'Sem resumo'}
                </span>
                <div className="shrink-0 text-right">
                  {typeof item.overall_score === 'number' && (
                    <span className={cn('text-xs font-bold tabular-nums', scoreColor(item.overall_score))}>
                      {item.overall_score}
                    </span>
                  )}
                  <p className="text-[10px] text-muted-foreground">{timeAgo}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SeoProfileAnalyzerPage() {
  const analyzer = useSeoProfileAnalysis();
  const [selectedAnalysis, setSelectedAnalysis] = useState<SeoProfileAnalysisResult | null>(null);

  const visibleAnalysis = selectedAnalysis ?? analyzer.currentAnalysis ?? analyzer.history[0] ?? null;

  const handleSelectHistory = (item: SeoProfileAnalysisResult) => {
    setSelectedAnalysis(item);
  };

  const handleNewAnalysis = async (payload: Parameters<typeof analyzer.analyzeProfile>[0]) => {
    setSelectedAnalysis(null);
    await analyzer.analyzeProfile(payload);
  };

  const historyItems = analyzer.history.filter((h) => h.id !== analyzer.currentAnalysis?.id);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <header className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-white to-emerald-50/40 px-6 py-6 shadow-sm dark:from-card dark:to-emerald-950/10">
        {/* Decorative chart illustration */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute right-4 top-4 h-20 w-28 opacity-10 sm:right-6 sm:top-6 sm:h-24 sm:w-36"
          viewBox="0 0 140 80"
          fill="none"
        >
          <rect x="0" y="52" width="16" height="28" rx="3" fill="currentColor" className="text-emerald-600" />
          <rect x="22" y="36" width="16" height="44" rx="3" fill="currentColor" className="text-emerald-600" />
          <rect x="44" y="20" width="16" height="60" rx="3" fill="currentColor" className="text-emerald-600" />
          <rect x="66" y="10" width="16" height="70" rx="3" fill="currentColor" className="text-emerald-600" />
          <rect x="88" y="28" width="16" height="52" rx="3" fill="currentColor" className="text-emerald-600" />
          <rect x="110" y="4" width="16" height="76" rx="3" fill="currentColor" className="text-emerald-600" />
          <polyline
            points="8,48 30,32 52,16 74,6 96,24 118,0"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-emerald-500"
          />
        </svg>

        <div className="space-y-1 pr-20 sm:pr-32">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-700">Diagnóstico de Presença Digital</p>
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Como sua clínica aparece online?
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Analise seu perfil no Google, site, Instagram ou Doctoralia e receba um diagnóstico com prioridades de melhoria.
          </p>
        </div>
      </header>

      {analyzer.analyzing && analyzer.isBackground && (
        <BackgroundBanner step={analyzer.analyzingStep} />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
        {/* Painel esquerdo — formulário + histórico */}
        <div className="space-y-4">
          <Card className="shadow-sm">
            <CardContent className="pt-5 pb-5">
              <SeoProfileForm
                analyzing={analyzer.analyzing}
                onSubmit={handleNewAnalysis}
              />
            </CardContent>
          </Card>

          {historyItems.length > 0 && (
            <HistoryDropdown
              items={historyItems}
              activeId={visibleAnalysis?.id}
              onSelect={handleSelectHistory}
            />
          )}
        </div>

        {/* Painel direito — resultado */}
        <div className="min-w-0">
          {analyzer.analyzing && !analyzer.isBackground ? (
            <AnalyzingCard
              step={analyzer.analyzingStep}
              onBackground={() => analyzer.setIsBackground(true)}
            />
          ) : (
            <SeoProfileResult analysis={visibleAnalysis} showDebug={false} />
          )}
        </div>
      </div>
    </div>
  );
}
